// TabComponents/DetailsTab.jsx
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Typography, Tag, Descriptions,
    Collapse, Button, Space, Avatar,
    Input, Form, Select, message,
    Divider, Tooltip
} from 'antd';
import {
    UserOutlined, CarOutlined, GlobalOutlined,
    ChromeOutlined, LaptopOutlined, ClusterOutlined,
    EnvironmentOutlined, SearchOutlined, EyeOutlined,
    CopyOutlined, ClockCircleOutlined,
    MailOutlined,
    PhoneOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    PlusOutlined,
    GoogleOutlined,
    BarChartOutlined,
    LineChartOutlined,
    CheckCircleOutlined,
    LockOutlined,
    CaretRightOutlined,
    CommentOutlined
} from '@ant-design/icons';
import API from "../../../../API/API"
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const DetailsTab = ({ lead, isAdmin, onOpenEmailModal,onUpdateLead, onRefresh }) => {

    const hasBrowserInfo = lead?.browserInfo?.userAgent || lead?.browserInfo?.platform;
    const hasLocationInfo = lead?.locationInfo?.ip;
    const loc = lead?.locationInfo || {};

    // Edit states
    const [isEditingVin, setIsEditingVin] = useState(false);
    const [vinValue, setVinValue] = useState(lead.vehicleInfo?.vin || '');
    const [savingVin, setSavingVin] = useState(false);
    
    // Combined edit state for Personal and Vehicle together
    const [isEditingCombined, setIsEditingCombined] = useState(false);
    const [combinedForm] = Form.useForm();
    const [savingCombined, setSavingCombined] = useState(false);
    
    const [isEditingSpec, setIsEditingSpec] = useState(false);
    const [specValue, setSpecValue] = useState(lead.vehicleInfo?.specification || '');
    const [savingSpec, setSavingSpec] = useState(false);
const [isEditingMilesPromise, setIsEditingMilesPromise] = useState(false);
const [milesPromiseValue, setMilesPromiseValue] = useState('');
const [savingMilesPromise, setSavingMilesPromise] = useState(false);


const handleSaveMilesPromise = async () => {
    const trimmedValue = milesPromiseValue.trim();
    
    if (!trimmedValue) {
        message.warning('Please enter a comment');
        return;
    }
    
    if (trimmedValue.length > 1000) {
        message.warning('Comment cannot exceed 1000 characters');
        return;
    }

    setSavingMilesPromise(true);
    
    try {
        // Get existing comments
        const existingComments = lead?.milesPromiseCustomer || [];
        
        // Create new comment entry
        const newComment = {
            changedAt: new Date().toISOString(),
            comment: trimmedValue
        };
        
        // Add new comment to the array
        const updatedComments = [...existingComments, newComment];
        
        const payload = {
            milesPromiseCustomer: updatedComments
        };
        
        const response = await API.UpdateLead(lead._id, payload);
        
        if (response?.status) {
            message.success('Miles Promise customer comment added successfully!');
            setIsEditingMilesPromise(false);
            setMilesPromiseValue('');
            
            // Update local lead data
            if (onUpdateLead) {
                await onUpdateLead(lead._id, payload);
            } else if (onRefresh) {
                onRefresh();
            } else {
                if (lead.milesPromiseCustomer) {
                    lead.milesPromiseCustomer = updatedComments;
                }
                message.info('Please refresh the page to see the updated value');
            }
        } else {
            message.error(response?.message || 'Failed to add comment');
        }
    } catch (error) {
        console.error('Error saving miles promise comment:', error);
        message.error('Failed to save comment');
    } finally {
        setSavingMilesPromise(false);
    }
};

const handleCancelMilesPromiseEdit = () => {
    setMilesPromiseValue('');
    setIsEditingMilesPromise(false);
};

    // Add this helper function to get the effective zip code
const getEffectiveZipCode = () => {
    // First try personalInfo zipCode
    if (lead.personalInfo?.zipCode && lead.personalInfo.zipCode.trim() !== '') {
        return lead.personalInfo.zipCode;
    }
    // Fallback to billingAddress zipCode if available
    if (lead.billingAddress?.zip && lead.billingAddress.zip.trim() !== '') {
        return lead.billingAddress.zip;
    }
    return 'N/A';
};

    const handleSaveSpec = async () => {
        const trimmedValue = specValue.trim();
        
        if (trimmedValue.length > 2000) {
            message.warning('Specification cannot exceed 2000 characters');
            return;
        }

        setSavingSpec(true);
        
        try {
            const payload = {
                vehicleInfo: {
                    ...lead.vehicleInfo,
                    specification: trimmedValue
                }
            };
            
            const response = await API.UpdateLead(lead._id, payload);
            
            if (response?.status) {
                message.success('Specification updated successfully!');
                setIsEditingSpec(false);
                
                // Update local lead data if onUpdateLead is provided
                if (onUpdateLead) {
                    // Call onUpdateLead to refresh the parent component's data
                    await onUpdateLead(lead._id, payload);
                } else if (onRefresh) {
                    // If onRefresh is provided, call it to refresh the lead list
                    onRefresh();
                } else {
                    // Fallback: manually update the lead object
                    if (lead.vehicleInfo) {
                        lead.vehicleInfo.specification = trimmedValue;
                    }
                    message.info('Please refresh the page to see the updated value');
                }
            } else {
                message.error(response?.message || 'Failed to update specification');
            }
        } catch (error) {
            console.error('Error saving specification:', error);
            message.error('Failed to save specification');
        } finally {
            setSavingSpec(false);
        }
    };

    const handleCancelSpecEdit = () => {
        setSpecValue(lead.vehicleInfo?.specification || '');
        setIsEditingSpec(false);
    };
    
    const handleSaveVin = async () => {
        if (!vinValue.trim()) {
            message.warning('Please enter a VIN number');
            return;
        }

        // Validate VIN format (basic validation)
        if (vinValue.length !== 17) {
            message.warning('VIN must be 17 characters long');
            return;
        }

        try {
            setSavingVin(true);

            // Call the update function passed from parent
            if (onUpdateLead) {
                const updatedLead = await onUpdateLead(lead._id, {
                    vehicleInfo: {
                        ...lead.vehicleInfo,
                        vin: vinValue.trim().toUpperCase()
                    }
                });

                if (updatedLead) {
                    message.success('VIN updated successfully!');
                    setIsEditingVin(false);
                }
            } else {
                message.error('Update function not available');
            }
        } catch (error) {
            console.error('Error saving VIN:', error);
            message.error('Failed to save VIN');
        } finally {
            setSavingVin(false);
        }
    };

    // Function to cancel editing
    const handleCancelEditVin = () => {
        setVinValue(lead.vehicleInfo?.vin || '');
        setIsEditingVin(false);
    };

    // ZIP Code validation function - optional
    const validateZipCode = (_, value) => {
        if (!value) return Promise.resolve(); // Allow empty
        
        // Regex for 12345 or 12345-2345 format
        const zipRegex = /^\d{5}(-\d{4})?$/;
        if (zipRegex.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Please enter a valid ZIP code (12345 or 12345-2345)'));
    };

    // VIN validation function - optional, only validate if value exists
    const validateVin = (_, value) => {
        if (!value) {
            return Promise.resolve(); // Allow empty
        }
        
        // Remove any whitespace and convert to uppercase for validation
        const cleanVin = value.trim().toUpperCase();
        
        // Check if exactly 17 characters
        if (cleanVin.length !== 17) {
            return Promise.reject(new Error('VIN must be exactly 17 characters'));
        }
        
        // Check if contains only allowed characters (alphanumeric, excluding I, O, Q to avoid confusion)
        const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/; // Excludes I, O, Q
        if (!vinRegex.test(cleanVin)) {
            return Promise.reject(new Error('VIN contains invalid characters (I, O, Q are not allowed)'));
        }
        
        return Promise.resolve();
    };
const handleSaveCombined = async (values) => {
    try {
        setSavingCombined(true);
        
        // Clean and uppercase VIN
        const cleanVin = values.vin ? values.vin.trim().toUpperCase() : '';
        
        // Prepare the payload
        let payload = {
            personalInfo: {
                ...lead.personalInfo,
                fullName: values.fullName,
                email: values.email,
                phoneNumber: values.phoneNumber,
                zipCode: values.zipCode,
                customerTimeZone: lead.personalInfo?.customerTimeZone || 'PST'
            },
            vehicleInfo: {
                ...lead.vehicleInfo,
                year: values.year,
                make: values.make,
                model: values.model,
                part: values.part,
                vin: cleanVin,
                specification: values.specification || lead.vehicleInfo?.specification
            },
            warranty: values.warranty
        };
        
        // Handle miles promise customer if provided
        if (values.milesPromiseCustomer && values.milesPromiseCustomer.trim() !== '') {
            const existingComments = lead?.milesPromiseCustomer || [];
            const newComment = {
                changedAt: new Date().toISOString(),
                comment: values.milesPromiseCustomer.trim()
            };
            payload.milesPromiseCustomer = [...existingComments, newComment];
        }
        
        if (onUpdateLead) {
            const updatedLead = await onUpdateLead(lead._id, payload);
            
            if (updatedLead) {
                message.success('Lead information updated successfully!');
                setIsEditingCombined(false);
                setVinValue(cleanVin);
            }
        } else {
            // Direct API call if onUpdateLead not provided
            const response = await API.UpdateLead(lead._id, payload);
            if (response?.status) {
                message.success('Lead information updated successfully!');
                setIsEditingCombined(false);
                setVinValue(cleanVin);
                if (onRefresh) onRefresh();
            } else {
                message.error(response?.message || 'Failed to update lead');
            }
        }
    } catch (error) {
        console.error('Error saving lead info:', error);
        message.error('Failed to save lead information');
    } finally {
        setSavingCombined(false);
    }
};

const startEditCombined = () => {
    // Get the effective zip code
    const effectiveZipCode = lead.personalInfo?.zipCode || 
                             lead.billingAddress?.zip || 
                             '';
    
    combinedForm.setFieldsValue({
        // Personal info
        fullName: lead.personalInfo?.fullName,
        email: lead.personalInfo?.email,
        phoneNumber: lead.personalInfo?.phoneNumber,
        zipCode: effectiveZipCode,  // Use the effective zip code
        
        // Vehicle info
        year: lead.vehicleInfo?.year,
        make: lead.vehicleInfo?.make,
        model: lead.vehicleInfo?.model,
        part: lead.vehicleInfo?.part,
        vin: lead.vehicleInfo?.vin || '',
        specification: lead.vehicleInfo?.specification || '',
        warranty: lead.warranty || 'No Warranty',
            milesPromiseCustomer: lead?.milesPromiseCustomer?.[lead.milesPromiseCustomer.length - 1]?.comment || '',
    });
    setIsEditingCombined(true);
};

    return (
        <Card
            style={{
                marginBottom: '24px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
        >
            <Collapse
                defaultActiveKey={['personal', 'vehicle', 'domain']}
                bordered={false}
                style={{ background: 'transparent' }}
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            >
                {/* Combined Personal and Vehicle Information Panel */}
                <Panel
                    header={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                paddingRight: '16px'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <UserOutlined style={{ color: '#1677ff', fontSize: '20px' }} />
                                <span
                                    style={{
                                        fontWeight: '600',
                                        fontSize: '16px',
                                        background: 'linear-gradient(90deg, #1677ff, #52c41a)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent'
                                    }}
                                >
                                    Personal & Vehicle Information
                                </span>
                            </div>
{!isEditingCombined && (lead.leadStage !== 'Sales Made' || isAdmin) && (
                                <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        startEditCombined();
                                    }}
                                    style={{
                                        color: '#1677ff',
                                        fontWeight: 500
                                    }}
                                >
                                    Edit
                                </Button>
                            )}
                        </div>
                    }
                    key="personal"
                    style={{
                        marginBottom: '14px',
                        borderRadius: '12px',
                        border: '1px solid #e6f4ff',
                        boxShadow: '0 6px 20px rgba(22,119,255,0.08)'
                    }}
                >
                    {isEditingCombined ? (
                        <Form
                            form={combinedForm}
                            layout="vertical"
                            onFinish={handleSaveCombined}
                            initialValues={{
                                // Personal info
                                fullName: lead.personalInfo?.fullName,
                                email: lead.personalInfo?.email,
                                phoneNumber: lead.personalInfo?.phoneNumber,
                                zipCode: lead.personalInfo?.zipCode,
                                
                                // Vehicle info
                                year: lead.vehicleInfo?.year,
                                make: lead.vehicleInfo?.make,
                                model: lead.vehicleInfo?.model,
                                part: lead.vehicleInfo?.part,
                                vin: lead.vehicleInfo?.vin || '',
                                specification: lead.vehicleInfo?.specification || '',
                                warranty: lead.warranty || 'No Warranty',
                                   milesPromiseCustomer: lead?.milesPromiseCustomer?.[lead.milesPromiseCustomer.length - 1]?.comment || '', 
                            }}
                        >
                            <Divider orientation="left" orientationMargin="0" style={{ color: '#1677ff', fontWeight: 600 }}>
                                Personal Information
                            </Divider>
                            
                            <Row gutter={[24, 18]}>
                                <Col span={12}>
                                    <Form.Item
                                        name="fullName"
                                        label={<Text style={{ fontWeight: 600, color: '#1e40af' }}>Full Name</Text>}
                                    >
                                        <Input 
                                            placeholder="Enter full name"
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#93c5fd'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="email"
                                        label={<Text style={{ fontWeight: 600, color: '#c2410c' }}>Email</Text>}
                                        rules={[
                                            { required: true, message: 'Email is required' },
                                        ]}
                                    >
                                        <Input 
                                            placeholder="Enter email"
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#fdba74'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="phoneNumber"
                                        label={<Text style={{ fontWeight: 600, color: '#166534' }}>Phone Number</Text>}
                                    >
                                        <Input 
                                            placeholder="Enter phone number"
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#86efac'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
    <Form.Item
        name="zipCode"
        label={<Text style={{ fontWeight: 600, color: '#9d174d' }}>Zip Code</Text>}
        rules={[
            { validator: validateZipCode }
        ]}
        tooltip={!lead.personalInfo?.zipCode && lead.billingAddress?.zip ? 
            "Currently using zip code from billing address" : 
            "Enter zip code (e.g., 12345 or 12345-6789)"}
    >
        <Input 
            placeholder="Enter zip code (e.g., 12345 or 12345-6789)"
            style={{
                borderRadius: '8px',
                borderColor: '#f9a8d4'
            }}
        />
    </Form.Item>
    {!lead.personalInfo?.zipCode && lead.billingAddress?.zip && (
        <div style={{ fontSize: '11px', color: '#9d174d', marginTop: '-12px' }}>
            Current value from billing address: {lead.billingAddress.zip}
        </div>
    )}
</Col>
                            </Row>

                            <Divider orientation="left" orientationMargin="0" style={{ color: '#fa541c', fontWeight: 600, marginTop: '24px' }}>
                                Vehicle Information
                            </Divider>

                            <Row gutter={[16, 16]}>
                                <Col span={6}>
                                    <Form.Item
                                        name="year"
                                        label={<Text style={{ fontWeight: 600, color: '#1e40af' }}>Year</Text>}
                                    >
                                        <Input 
                                            placeholder="e.g., 2020"
                                            maxLength={4}
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#93c5fd'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="make"
                                        label={<Text style={{ fontWeight: 600, color: '#166534' }}>Make</Text>}
                                    >
                                        <Input 
                                            placeholder="e.g., Toyota"
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#86efac'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="model"
                                        label={<Text style={{ fontWeight: 600, color: '#5b21b6' }}>Model</Text>}
                                    >
                                        <Input 
                                            placeholder="e.g., Camry"
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#c4b5fd'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="part"
                                        label={<Text style={{ fontWeight: 600, color: '#c2410c' }}>Part</Text>}
                                    >
                                        <Input 
                                            placeholder="e.g., Engine"
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#fdba74'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="vin"
                                        label={<Text style={{ fontWeight: 600, color: '#92400e' }}>VIN (Vehicle Identification Number)</Text>}
                                        validateTrigger="onBlur"
                                    >
                                        <Input 
                                            placeholder="Enter 17-character VIN (e.g., 1HGCM82633A123456)"
                                            maxLength={17}
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#fbbf24',
                                                fontFamily: 'monospace',
                                                letterSpacing: '1px',
                                                textTransform: 'uppercase'
                                            }}
                                            onChange={(e) => {
                                                // Auto-uppercase as user types
                                                e.target.value = e.target.value.toUpperCase();
                                            }}
                                        />
                                    </Form.Item>
                                    <div style={{ 
                                        fontSize: '11px', 
                                        color: '#92400e', 
                                        marginTop: '-12px',
                                        textAlign: 'right'
                                    }}>
                                        {combinedForm.getFieldValue('vin')?.length || 0}/17 characters
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="warranty"
                                        label={<Text style={{ fontWeight: 600, color: '#0f766e' }}>Warranty</Text>}
                                        initialValue="No Warranty"
                                    >
                                        <Select
                                            placeholder="Select warranty"
                                            style={{
                                                borderRadius: '8px'
                                            }}
                                            options={[
                                                { value: '30 Days', label: '30 Days' },
                                                { value: '60 Days', label: '60 Days' },
                                                { value: '90 Days', label: '90 Days' },
                                                { value: '120 Days', label: '120 Days' },
                                                { value: '150 Days', label: '150 Days' },
                                                { value: '180 Days', label: '180 Days' },
                                                { value: '1 Year', label: '1 Year' },
                                                { value: '2 Years', label: '2 Years' },
                                                { value: '3 Years', label: '3 Years' },
                                                    { value: '4 Years', label: '4 Years' },
                                                { value: '5 Years', label: '5 Years' },
                                                { value: 'No Warranty', label: 'No Warranty' }
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                    {lead.leadStage === "Sales Made" && (

                                <Col span={12}>
    <Form.Item
        name="milesPromiseCustomer"
        label={<Text style={{ fontWeight: 600, color: '#9333ea' }}>Miles Promise Customer</Text>}
        tooltip="Add or update the miles promise agreement with customer"
    >
        <Input.TextArea
            rows={3}
            placeholder="Enter miles promise agreement details with customer..."
            maxLength={1000}
            showCount
            style={{
                borderRadius: '8px',
                borderColor: '#d8b4fe'
            }}
        />
    </Form.Item>
</Col>
    )}

                                <Col span={24}>
                                    <Form.Item
                                        name="specification"
                                        label={<Text style={{ fontWeight: 600, color: '#0284c7' }}>Specification</Text>}
                                    >
                                        <Input.TextArea
                                            rows={3}
                                            placeholder="Enter vehicle specification details (e.g., Color: Red, Transmission: Automatic, Fuel Type: Petrol, Features: Navigation, Sunroof, etc.)"
                                            maxLength={2000}
                                            showCount
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#38bdf8'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Row>
                                <Col span={24}>
                                    <Space size="middle" style={{ justifyContent: 'flex-end', display: 'flex' }}>
                                        <Button
                                            icon={<CloseOutlined />}
                                            onClick={() => setIsEditingCombined(false)}
                                            disabled={savingCombined}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SaveOutlined />}
                                            loading={savingCombined}
                                            style={{
                                                backgroundColor: '#059669',
                                                borderColor: '#059669'
                                            }}
                                        >
                                            Save All Changes
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    ) : (
                        <>
                            <Divider orientation="left" orientationMargin="0" style={{ color: '#1677ff', fontWeight: 600 }}>
                                Personal Information
                            </Divider>
                            
                            <Row gutter={[24, 18]}>
                                {/* FULL NAME - Royal Blue Theme */}
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af' }}>
                                        Full Name
                                    </Text>
                                    <div
                                        style={{
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #dbeafe, #ffffff)',
                                            border: '1px solid #93c5fd',
                                            boxShadow: '0 2px 8px rgba(30,64,175,0.1)'
                                        }}
                                    >
                                        <Text strong style={{ fontSize: '15px', color: '#1e3a8a' }}>
                                            {lead.personalInfo?.fullName || 'N/A'}
                                        </Text>
                                    </div>
                                </Col>

                                {/* EMAIL - Orange Sunset Theme */}
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#c2410c' }}>
                                        Email
                                    </Text>
                                    <div
                                        style={{
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #ffedd5, #ffffff)',
                                            border: '1px solid #fdba74',
                                            boxShadow: '0 2px 8px rgba(194,65,12,0.1)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <MailOutlined style={{ color: '#ea580c' }} />
                                                <Text style={{ color: '#7c2d12' }}>{lead.personalInfo?.email || 'N/A'}</Text>
                                            </div>

                                            {lead.personalInfo?.email && onOpenEmailModal && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={onOpenEmailModal}
                                                    style={{
                                                        color: '#ea580c',
                                                        padding: '0 8px',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    Send Email
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                                {/* PHONE - Forest Green Theme */}
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#166534' }}>
                                        Phone Number
                                    </Text>
                                    <div
                                        style={{
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #dcfce7, #ffffff)',
                                            border: '1px solid #86efac',
                                            boxShadow: '0 2px 8px rgba(22,101,52,0.1)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <PhoneOutlined style={{ color: '#16a34a', fontSize: '16px' }} />

                                            {lead.personalInfo?.phoneNumber ? (
                                                <a
                                                    href={`tel:+${lead.personalInfo.phoneNumber}`}
                                                    style={{
                                                        color: '#14532d',
                                                        fontWeight: 600,
                                                        fontSize: '15px',
                                                        textDecoration: 'none'
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.textDecoration = 'underline';
                                                        e.currentTarget.style.color = '#15803d';
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.textDecoration = 'none';
                                                        e.currentTarget.style.color = '#14532d';
                                                    }}
                                                >
                                                    +{lead.personalInfo.phoneNumber}
                                                    <span style={{ color: '#16a34a', marginLeft: '6px', fontWeight: 400 }}>
                                                        → click here to call
                                                    </span>
                                                </a>
                                            ) : (
                                                <Text style={{ color: '#9ca3af', fontSize: '15px' }}>N/A</Text>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                               {/* ZIP CODE - Rose/Pink Theme */}
<Col span={12}>
    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#9d174d' }}>
        Zip Code
    </Text>
    <div
        style={{
            padding: '10px 14px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #fce7f3, #ffffff)',
            border: '1px solid #f9a8d4',
            boxShadow: '0 2px 8px rgba(157,23,77,0.1)'
        }}
    >
        <Text style={{ color: '#831843', fontWeight: 500, fontSize: '15px' }}>
            {getEffectiveZipCode()}
        </Text>
        {/* Optional: Add a tooltip to show source */}
        {lead.billingAddress?.zip && !lead.personalInfo?.zipCode && (
            <Tooltip title="Using zip code from billing address">
                <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                    (from billing)
                </Text>
            </Tooltip>
        )}
    </div>
</Col>

                                {/* LEAD TYPE - Teal/Turquoise Theme */}
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#0f5e5e' }}>
                                        Lead Type
                                    </Text>
                                    <div
                                        style={{
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            background:
                                                lead.leadType === 'inbound'
                                                    ? 'linear-gradient(135deg, #ccfbf1, #ffffff)'
                                                    : 'linear-gradient(135deg, #e0f2fe, #ffffff)',
                                            border:
                                                lead.leadType === 'inbound'
                                                    ? '1px solid #5eead4'
                                                    : '1px solid #7dd3fc',
                                            boxShadow: '0 2px 8px rgba(15,94,94,0.1)'
                                        }}
                                    >
                                        <Text style={{ 
                                            color: lead.leadType === 'inbound' ? '#115e59' : '#0369a1',
                                            fontWeight: 600,
                                            fontSize: '15px'
                                        }}>
                                            {lead.leadType?.toUpperCase() || 'N/A'}
                                        </Text>
                                    </div>
                                </Col>
                            </Row>

                            <Divider orientation="left" orientationMargin="0" style={{ color: '#fa541c', fontWeight: 600, marginTop: '24px' }}>
                                Vehicle Information
                            </Divider>

                            <Row gutter={[16, 16]}>
                                {/* YEAR - Deep Blue Theme */}
                                <Col span={6}>
                                    <div style={{
                                        padding: '16px 12px',
                                        textAlign: 'center',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #dbeafe, #ffffff)',
                                        border: '1px solid #93c5fd',
                                        boxShadow: '0 4px 12px rgba(30,64,175,0.12)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'default',
                                        height: '100%'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <Text type="secondary" style={{ 
                                            fontSize: '12px', 
                                            marginBottom: '8px', 
                                            display: 'block',
                                            color: '#1e40af',
                                            fontWeight: 600,
                                            letterSpacing: '0.3px'
                                        }}>
                                            YEAR
                                        </Text>
                                        <Text strong style={{ 
                                            fontSize: '24px', 
                                            color: '#1e3a8a',
                                            textShadow: '0 2px 4px rgba(30,64,175,0.2)'
                                        }}>
                                            {lead.vehicleInfo?.year || 'N/A'}
                                        </Text>
                                    </div>
                                </Col>

                                {/* MAKE - Forest Green Theme */}
                                <Col span={6}>
                                    <div style={{
                                        padding: '16px 12px',
                                        textAlign: 'center',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #dcfce7, #ffffff)',
                                        border: '1px solid #86efac',
                                        boxShadow: '0 4px 12px rgba(22,101,52,0.12)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'default',
                                        height: '100%'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <Text type="secondary" style={{ 
                                            fontSize: '12px', 
                                            marginBottom: '8px', 
                                            display: 'block',
                                            color: '#166534',
                                            fontWeight: 600,
                                            letterSpacing: '0.3px'
                                        }}>
                                            MAKE
                                        </Text>
                                        <Text strong style={{ 
                                            fontSize: '24px', 
                                            color: '#14532d',
                                            textShadow: '0 2px 4px rgba(22,101,52,0.2)'
                                        }}>
                                            {lead.vehicleInfo?.make || 'N/A'}
                                        </Text>
                                    </div>
                                </Col>

                                {/* MODEL - Purple Lavender Theme */}
                                <Col span={6}>
                                    <div style={{
                                        padding: '16px 12px',
                                        textAlign: 'center',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #ede9fe, #ffffff)',
                                        border: '1px solid #c4b5fd',
                                        boxShadow: '0 4px 12px rgba(91,33,182,0.12)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'default',
                                        height: '100%'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <Text type="secondary" style={{ 
                                            fontSize: '12px', 
                                            marginBottom: '8px', 
                                            display: 'block',
                                            color: '#5b21b6',
                                            fontWeight: 600,
                                            letterSpacing: '0.3px'
                                        }}>
                                            MODEL
                                        </Text>
                                        <Text strong style={{ 
                                            fontSize: '24px', 
                                            color: '#4c1d95',
                                            textShadow: '0 2px 4px rgba(91,33,182,0.2)'
                                        }}>
                                            {lead.vehicleInfo?.model || 'N/A'}
                                        </Text>
                                    </div>
                                </Col>

                                {/* PART - Sunset Orange Theme */}
                                <Col span={6}>
                                    <div style={{
                                        padding: '16px 12px',
                                        textAlign: 'center',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #ffedd5, #ffffff)',
                                        border: '1px solid #fdba74',
                                        boxShadow: '0 4px 12px rgba(194,65,12,0.15)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'default',
                                        height: '100%'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <Text type="secondary" style={{ 
                                            fontSize: '12px', 
                                            marginBottom: '8px', 
                                            display: 'block',
                                            color: '#c2410c',
                                            fontWeight: 600,
                                            letterSpacing: '0.3px'
                                        }}>
                                            PART
                                        </Text>
                                        <Text strong style={{ 
                                            fontSize: '24px', 
                                            color: '#7c2d12',
                                            textShadow: '0 2px 4px rgba(194,65,12,0.2)'
                                        }}>
                                            {lead.vehicleInfo?.part || 'N/A'}
                                        </Text>
                                    </div>
                                </Col>

                                {/* VIN - Gold/Amber Theme (full width) */}
                                <Col span={24}>
                                    <div style={{
                                        borderRadius: '14px',
                                        background: 'linear-gradient(135deg, #fef3c7, #ffffff)',
                                        border: '2px solid #fcd34d',
                                        boxShadow: '0 6px 18px rgba(180,83,9,0.2)',
                                        marginTop: '8px',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <div style={{ textAlign: 'center', padding: '16px' }}>
                                            <Text
                                                type="secondary"
                                                style={{
                                                    fontSize: '12px',
                                                    marginBottom: '10px',
                                                    display: 'block',
                                                    letterSpacing: '1px',
                                                    fontWeight: 700,
                                                    color: '#92400e',
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                VEHICLE IDENTIFICATION NUMBER (VIN)
                                            </Text>

                                            {/* VIEW MODE */}
                                            {!isEditingVin && (
                                                <>
                                                    <div style={{
                                                        marginBottom: '16px',
                                                        minHeight: '32px',
                                                        padding: '8px 16px',
                                                        background: 'rgba(252,211,77,0.1)',
                                                        borderRadius: '8px',
                                                        display: 'inline-block'
                                                    }}>
                                                        <Text
                                                            strong
                                                            style={{
                                                                fontSize: '20px',
                                                                color: '#b45309',
                                                                wordBreak: 'break-all',
                                                                fontFamily: 'monospace',
                                                                letterSpacing: '2px',
                                                                textShadow: '0 1px 2px rgba(180,83,9,0.2)'
                                                            }}
                                                        >
                                                            {vinValue || 'No VIN Provided'}
                                                        </Text>
                                                    </div>

                                                    <Space size="middle">
{(lead.leadStage !== 'Sales Made' || isAdmin) && (
                                                            <Button
                                                                type="primary"
                                                                icon={vinValue ? <EditOutlined /> : <PlusOutlined />}
                                                                onClick={() => setIsEditingVin(true)}
                                                                style={{
                                                                    backgroundColor: '#d97706',
                                                                    borderColor: '#d97706',
                                                                    fontWeight: 600,
                                                                    boxShadow: '0 2px 8px rgba(217,119,6,0.3)'
                                                                }}
                                                            >
                                                                {vinValue ? 'Edit VIN' : 'Add VIN'}
                                                            </Button>
                                                        )}

                                                        {vinValue && (
                                                            <Button
                                                                icon={<CopyOutlined />}
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(vinValue);
                                                                    message.success('VIN copied to clipboard!');
                                                                }}
                                                                style={{
                                                                    borderColor: '#d97706',
                                                                    color: '#d97706'
                                                                }}
                                                            >
                                                                Copy
                                                            </Button>
                                                        )}
                                                    </Space>
                                                </>
                                            )}

                                            {/* EDIT MODE */}
                                            {isEditingVin && (
                                                <>
                                                    <div style={{ marginBottom: '16px', maxWidth: '400px', margin: '0 auto 16px' }}>
                                                        <Input
                                                            value={vinValue}
                                                            onChange={e => setVinValue(e.target.value.toUpperCase())}
                                                            placeholder="Enter 17-character VIN"
                                                            maxLength={17}
                                                            style={{
                                                                textAlign: 'center',
                                                                fontWeight: 600,
                                                                fontSize: '18px',
                                                                fontFamily: 'monospace',
                                                                letterSpacing: '3px',
                                                                height: '46px',
                                                                borderColor: '#fbbf24',
                                                                boxShadow: '0 2px 8px rgba(251,191,36,0.2)'
                                                            }}
                                                            autoFocus
                                                        />
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: '#92400e',
                                                            marginTop: '6px',
                                                            textAlign: 'center',
                                                            fontWeight: 500
                                                        }}>
                                                            {vinValue.length}/17 characters
                                                        </div>
                                                    </div>

                                                    <Space size="middle">
                                                        <Button
                                                            type="primary"
                                                            icon={<SaveOutlined />}
                                                            onClick={handleSaveVin}
                                                            loading={savingVin}
                                                            disabled={vinValue.length !== 17}
                                                            style={{
                                                                backgroundColor: '#059669',
                                                                borderColor: '#059669',
                                                                fontWeight: 600,
                                                                boxShadow: '0 2px 8px rgba(5,150,105,0.3)'
                                                            }}
                                                        >
                                                            Save VIN
                                                        </Button>
                                                        <Button
                                                            icon={<CloseOutlined />}
                                                            onClick={handleCancelEditVin}
                                                            disabled={savingVin}
                                                            style={{
                                                                borderColor: '#9ca3af'
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Space>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                                {/* Specification Field */}
                                <Col span={24}>
                                    <div style={{
                                        borderRadius: '14px',
                                        background: 'linear-gradient(135deg, #e0f2fe, #ffffff)',
                                        border: '2px solid #38bdf8',
                                        boxShadow: '0 6px 18px rgba(56,189,248,0.2)',
                                        marginTop: '8px',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <div style={{ textAlign: 'center', padding: '16px' }}>
                                            <Text
                                                type="secondary"
                                                style={{
                                                    fontSize: '12px',
                                                    marginBottom: '10px',
                                                    display: 'block',
                                                    letterSpacing: '1px',
                                                    fontWeight: 700,
                                                    color: '#075985',
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                VEHICLE SPECIFICATION
                                            </Text>

                                            {/* VIEW MODE */}
                                            {!isEditingSpec && (
                                                <>
                                                    <div style={{
                                                        marginBottom: '16px',
                                                        minHeight: '32px',
                                                        padding: '12px 20px',
                                                        background: 'rgba(56,189,248,0.08)',
                                                        borderRadius: '12px',
                                                        display: 'block',
                                                        textAlign: 'left'
                                                    }}>
                                                        {lead.vehicleInfo?.specification ? (
                                                            <Tooltip
                                                                title={
                                                                    <div style={{
                                                                        maxWidth: 400,
                                                                        maxHeight: 300,
                                                                        overflow: 'auto',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                        fontSize: '12px'
                                                                    }}>
                                                                        {lead.vehicleInfo.specification}
                                                                    </div>
                                                                }
                                                                placement="top"
                                                                color="#075985"
                                                            >
                                                                <Text
                                                                    style={{
                                                                        fontSize: '14px',
                                                                        color: '#0c4a6e',
                                                                        lineHeight: '1.6',
                                                                        wordBreak: 'break-word',
                                                                        whiteSpace: 'pre-wrap',
                                                                        display: 'block',
                                                                        fontFamily: 'system-ui, -apple-system, sans-serif'
                                                                    }}
                                                                >
                                                                    {lead.vehicleInfo.specification.length > 200 ? (
                                                                        <>
                                                                            {lead.vehicleInfo.specification.substring(0, 200)}...
                                                                            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '4px' }}>
                                                                                (hover to see full text)
                                                                            </Text>
                                                                        </>
                                                                    ) : (
                                                                        lead.vehicleInfo.specification
                                                                    )}
                                                                </Text>
                                                            </Tooltip>
                                                        ) : (
                                                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                                                <Text
                                                                    type="secondary"
                                                                    style={{
                                                                        fontSize: '14px',
                                                                        color: '#9ca3af',
                                                                        fontStyle: 'italic'
                                                                    }}
                                                                >
                                                                    No specification details added yet
                                                                </Text>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <Space size="middle">
{(lead.leadStage !== 'Sales Made' || isAdmin) && (
                                                            <Button
                                                                type="primary"
                                                                icon={lead.vehicleInfo?.specification ? <EditOutlined /> : <PlusOutlined />}
                                                                onClick={() => {
                                                                    setIsEditingSpec(true);
                                                                    setSpecValue(lead.vehicleInfo?.specification || '');
                                                                }}
                                                                style={{
                                                                    backgroundColor: '#0284c7',
                                                                    borderColor: '#0284c7',
                                                                    fontWeight: 600,
                                                                    boxShadow: '0 2px 8px rgba(2,132,199,0.3)'
                                                                }}
                                                            >
                                                                {lead.vehicleInfo?.specification ? 'Edit Specification' : 'Add Specification'}
                                                            </Button>
                                                        )}

                                                        {lead.vehicleInfo?.specification && (
                                                            <Button
                                                                icon={<CopyOutlined />}
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(lead.vehicleInfo.specification);
                                                                    message.success('Specification copied to clipboard!');
                                                                }}
                                                                style={{
                                                                    borderColor: '#0284c7',
                                                                    color: '#0284c7'
                                                                }}
                                                            >
                                                                Copy
                                                            </Button>
                                                        )}
                                                    </Space>
                                                </>
                                            )}

                                            {/* EDIT MODE */}
                                            {isEditingSpec && (
                                                <>
                                                    <div style={{ marginBottom: '16px', maxWidth: '100%', margin: '0 auto 16px' }}>
                                                        <Input.TextArea
                                                            value={specValue}
                                                            onChange={e => setSpecValue(e.target.value)}
                                                            placeholder="Enter vehicle specification details (e.g., Color: Red, Transmission: Automatic, Fuel Type: Petrol, Features: Navigation, Sunroof, etc.)"
                                                            maxLength={2000}
                                                            showCount={{
                                                                formatter: ({ count, maxLength }) => `${count}/${maxLength} characters`
                                                            }}
                                                            autoSize={{ minRows: 4, maxRows: 8 }}
                                                            style={{
                                                                fontWeight: 500,
                                                                fontSize: '14px',
                                                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                                                lineHeight: '1.6',
                                                                borderColor: '#38bdf8',
                                                                boxShadow: '0 2px 8px rgba(56,189,248,0.2)',
                                                                resize: 'vertical'
                                                            }}
                                                            autoFocus
                                                        />
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: '#075985',
                                                            marginTop: '8px',
                                                            textAlign: 'center',
                                                            fontWeight: 500,
                                                            background: 'rgba(56,189,248,0.1)',
                                                            padding: '6px',
                                                            borderRadius: '6px'
                                                        }}>
                                                            💡 Include details like color, transmission, fuel type, features, condition, etc.
                                                        </div>
                                                    </div>

                                                    <Space size="middle">
                                                        <Button
                                                            type="primary"
                                                            icon={<SaveOutlined />}
                                                            onClick={handleSaveSpec}
                                                            loading={savingSpec}
                                                            disabled={specValue.length > 2000}
                                                            style={{
                                                                backgroundColor: '#059669',
                                                                borderColor: '#059669',
                                                                fontWeight: 600,
                                                                boxShadow: '0 2px 8px rgba(5,150,105,0.3)'
                                                            }}
                                                        >
                                                            Save Specification
                                                        </Button>
                                                        <Button
                                                            icon={<CloseOutlined />}
                                                            onClick={handleCancelSpecEdit}
                                                            disabled={savingSpec}
                                                            style={{
                                                                borderColor: '#9ca3af'
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Space>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                                {/* Warranty Field */}
                                <Col span={24}>
                                    <div style={{
                                        borderRadius: '14px',
                                        background: 'linear-gradient(135deg, #ccfbf1, #ffffff)',
                                        border: '2px solid #5eead4',
                                        boxShadow: '0 6px 18px rgba(15,118,110,0.15)',
                                        marginTop: '8px',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <div style={{ padding: '16px 24px' }}>
                                            <Row align="middle" gutter={[16, 16]}>
                                                <Col xs={24} sm={8} md={6}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <CheckCircleOutlined style={{ color: '#0f766e', fontSize: '24px' }} />
                                                        <div>
                                                            <Text type="secondary" style={{ 
                                                                fontSize: '12px', 
                                                                display: 'block',
                                                                color: '#115e59',
                                                                fontWeight: 600,
                                                                letterSpacing: '0.3px',
                                                                textTransform: 'uppercase'
                                                            }}>
                                                                WARRANTY
                                                            </Text>
                                                            <Text strong style={{ 
                                                                fontSize: '18px', 
                                                                color: '#134e4a',
                                                                display: 'block'
                                                            }}>
                                                                Coverage Period
                                                            </Text>
                                                        </div>
                                                    </div>
                                                </Col>
                                                
                                                <Col xs={24} sm={16} md={18}>
                                                    {lead?.warranty ? (
                                                        <Space size="large" align="center" wrap>
                                                            <Tag
                                                                color="#0f766e"
                                                                style={{
                                                                    padding: '8px 24px',
                                                                    fontSize: '20px',
                                                                    fontWeight: 'bold',
                                                                    borderRadius: '30px',
                                                                    boxShadow: '0 2px 8px rgba(15,118,110,0.3)'
                                                                }}
                                                            >
                                                                {lead.warranty}
                                                            </Tag>
                                                            <Text style={{ color: '#115e59', fontSize: '16px' }}>
                                                                {lead.warranty === 'No Warranty' ? (
                                                                    'No warranty coverage'
                                                                ) : lead.warranty === '1 Year' ? (
                                                                    'Full 1 year warranty coverage'
                                                                ) : (
                                                                    `${lead.warranty} warranty coverage`
                                                                )}
                                                            </Text>
                                                        </Space>
                                                    ) : (
                                                        <Space align="center">
                                                            <Tag
                                                                style={{
                                                                    padding: '6px 20px',
                                                                    fontSize: '16px',
                                                                    backgroundColor: '#f1f5f9',
                                                                    borderColor: '#cbd5e1',
                                                                    color: '#64748b',
                                                                    borderRadius: '30px'
                                                                }}
                                                            >
                                                                Not Specified
                                                            </Tag>
                                                            <Text type="secondary" style={{ fontSize: '16px' }}>
                                                                No warranty information (default: No Warranty)
                                                            </Text>
                                                        </Space>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>
                                </Col>

     {/* MILES PROMISE CUSTOMER FIELD - Editable after Sales Made */}
<Col span={24}>
    <div style={{
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #fae8ff, #ffffff)',
        border: '2px solid #d8b4fe',
        boxShadow: '0 6px 18px rgba(168,85,247,0.15)',
        marginTop: '12px',
        transition: 'all 0.3s ease'
    }}
    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.01)'}
    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
    >
        <div style={{ padding: '16px 24px' }}>
            <Row align="top" gutter={[16, 16]}>
                <Col xs={24} sm={8} md={6}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CommentOutlined style={{ color: '#9333ea', fontSize: '24px' }} />
                        <div>
                            <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                display: 'block',
                                color: '#6b21a5',
                                fontWeight: 600,
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase'
                            }}>
                                MILES PROMISE
                            </Text>
                            <Text strong style={{ 
                                fontSize: '18px', 
                                color: '#581c87',
                                display: 'block'
                            }}>
                                Customer Agreement
                            </Text>
                        </div>
                    </div>
                </Col>
                
                <Col xs={24} sm={16} md={18}>
                    {/* EDIT BUTTON - Always visible for Admin or even for Sales Made */}
                                {lead.leadStage === "Sales Made" && (
        <div style={{ marginBottom: '16px', textAlign: 'right' }}>
            <Button
                type="text"
                icon={<PlusOutlined />}
                onClick={() => setIsEditingMilesPromise(true)}
                style={{
                    color: '#9333ea',
                    fontWeight: 500
                }}
            >
                Add Comment
            </Button>
        </div>
    )}
                    {/* EDIT MODE */}
                    {isEditingMilesPromise && (
                        <div style={{ marginBottom: '24px' }}>
                            <Input.TextArea
                                value={milesPromiseValue}
                                onChange={e => setMilesPromiseValue(e.target.value)}
                                placeholder="Enter miles promise agreement details with customer..."
                                maxLength={1000}
                                showCount
                                autoSize={{ minRows: 3, maxRows: 6 }}
                                style={{
                                    borderRadius: '12px',
                                    borderColor: '#d8b4fe',
                                    marginBottom: '12px'
                                }}
                                autoFocus
                            />
                            <Space size="middle">
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    onClick={handleSaveMilesPromise}
                                    loading={savingMilesPromise}
                                    disabled={!milesPromiseValue.trim()}
                                    style={{
                                        backgroundColor: '#9333ea',
                                        borderColor: '#9333ea'
                                    }}
                                >
                                    Save Comment
                                </Button>
                                <Button
                                    icon={<CloseOutlined />}
                                    onClick={handleCancelMilesPromiseEdit}
                                    disabled={savingMilesPromise}
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </div>
                    )}

                    {/* VIEW MODE - Show existing comments */}
                    {!isEditingMilesPromise && (
                        <>
                            {lead?.milesPromiseCustomer && lead.milesPromiseCustomer.length > 0 ? (
                                <div>
                                    {/* Show the most recent comment first */}
                                    {[...lead.milesPromiseCustomer].reverse().map((item, index) => (
                                        <div key={index} style={{
                                            marginBottom: index === lead.milesPromiseCustomer.length - 1 ? 0 : '16px',
                                            padding: '12px',
                                            backgroundColor: '#faf5ff',
                                            borderRadius: '12px',
                                            borderLeft: `4px solid ${index === 0 ? '#9333ea' : '#c084fc'}`
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                <ClockCircleOutlined style={{ color: '#9333ea', fontSize: '12px' }} />
                                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                                    {dayjs(item.changedAt).format('MMM DD, YYYY hh:mm A')}
                                                </Text>
                                                {index === 0 && (
                                                    <Tag color="purple" size="small" style={{ fontSize: '10px' }}>
                                                        Latest
                                                    </Tag>
                                                )}
                                            </div>
                                            <Text style={{ 
                                                fontSize: '14px', 
                                                color: '#4c1d95',
                                                lineHeight: '1.6',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}>
                                                {item.comment}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Text
                                        type="secondary"
                                        style={{
                                            fontSize: '14px',
                                            color: '#9ca3af',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        No miles promise customer agreement recorded yet
                                    </Text>
                                </div>
                            )}
                        </>
                    )}
                </Col>
            </Row>
        </div>
    </div>
</Col>
                            </Row>
                        </>
                    )}
                </Panel>

                {isAdmin && hasBrowserInfo && (
                    <>
                        <Panel
                            header={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <GoogleOutlined style={{ color: '#722ed1', fontSize: '18px' }} />
                                    <span
                                        style={{
                                            fontWeight: '600',
                                            fontSize: '16px',
                                            background: 'linear-gradient(90deg, #722ed1, #52c41a)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}
                                    >
                                        Domain & Campaign Information
                                    </span>
                                </div>
                            }
                            key="campaign"
                            style={{
                                marginBottom: 12,
                                borderRadius: 10,
                                border: '1px solid #b5f5ec',
                                boxShadow: '0 4px 16px rgba(19,194,194,0.12)'
                            }}
                        >
                            {/* Your  campaign info content */}
                            <Row gutter={[24, 16]}>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#c2410c' }}>
                                        Domain
                                    </Text>
                                    <div style={{ padding: '12px 16px', borderRadius: '10px', background: 'linear-gradient(135deg, #fff1f0, #ffffff)', border: '1px solid #ffbb96' }}>
                                        {lead.domain || 'N/A'}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#0f5e5e' }}>
                                        Campaign Name
                                    </Text>
                                    <div style={{ padding: '12px 16px', borderRadius: '10px', background: 'linear-gradient(135deg, #e6fffb, #ffffff)', border: '1px solid #87e8de' }}>
                                        {lead?.campaignName || "N/A"}
                                    </div>
                                </Col>
                                <Col span={24}>
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, color: '#0050b3' }}>
                                        Landing URL
                                    </Text>
                                    <div style={{ padding: '14px 18px', borderRadius: '10px', background: 'linear-gradient(135deg, #e6f7ff, #ffffff)', border: '1px solid #91d5ff' }}>
                                        {lead?.landingUrl || "N/A"}
                                    </div>
                                </Col>
                            </Row>
                        </Panel>

                             
                                  <Panel
  header={
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <ChromeOutlined style={{ color: '#f4389f', fontSize: '18px' }} />
      <span
        style={{
          fontWeight: '600',
          fontSize: '16px',
          background: 'linear-gradient(90deg, #f4389f, #52c41a)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
      >
        Browser Information
      </span>
    </div>
  }
  key="browser"
  style={{
    marginBottom: 12,
    borderRadius: 10,
    border: '1px solid #ffe7ba',
    boxShadow: '0 4px 16px rgba(250,84,28,0.12)'
  }}
>
  <Descriptions column={2} bordered size="small">

    <Descriptions.Item label="User Agent" span={2}>
      {lead.browserInfo?.userAgent || 'N/A'}
    </Descriptions.Item>

    <Descriptions.Item label="Platform">
      {lead.browserInfo?.platform || 'N/A'}
    </Descriptions.Item>

    <Descriptions.Item label="Language">
      {lead.browserInfo?.language || 'N/A'}
    </Descriptions.Item>

    <Descriptions.Item label="Languages">
      {(lead.browserInfo?.languages || []).join(', ') || 'N/A'}
    </Descriptions.Item>

    <Descriptions.Item label="Cookies Enabled">
      {lead.browserInfo?.cookieEnabled ? 'Yes' : 'No'}
    </Descriptions.Item>

    <Descriptions.Item label="Java Enabled">
      {lead.browserInfo?.javaEnabled ? 'Yes' : 'No'}
    </Descriptions.Item>

    <Descriptions.Item label="PDF Viewer">
      {lead.browserInfo?.pdfViewerEnabled ? 'Yes' : 'No'}
    </Descriptions.Item>

  </Descriptions>
</Panel>
<Panel
  header={
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <EnvironmentOutlined style={{ color: '#f28007', fontSize: 18 }} />
      <span
        style={{
          fontWeight: '600',
          fontSize: '16px',
          background: 'linear-gradient(90deg, #f28007, #52c41a)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
      >
        Location Information
      </span>
    </div>
  }
  key="location"
>
  <Descriptions column={2} bordered size="small">
    
    {/*  BASIC INFORMATION */}
    <Descriptions.Item label={<strong> BASIC INFORMATION</strong>} span={2} />
    
    <Descriptions.Item label="IP">
      {loc.ip || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Type">
      {loc.type || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Success">
      {loc.success ? 'Yes' : 'No'}
    </Descriptions.Item>
 <Descriptions.Item >
    </Descriptions.Item>
    {/*  LOCATION DETAILS */}
    <Descriptions.Item label={<strong> LOCATION DETAILS</strong>} span={2} />
    
    <Descriptions.Item label="Country">
      {loc.country || 'N/A'} {loc.flag?.emoji}
    </Descriptions.Item>
    
    <Descriptions.Item label="Country Code">
      {loc.country_code || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Capital">
      {loc.capital || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Calling Code">
      {loc.calling_code ? `+${loc.calling_code}` : 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="EU Member">
      {loc.is_eu ? 'Yes' : 'No'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Region">
      {loc.region || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Region Code">
      {loc.region_code || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="City">
      {loc.city || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Postal Code">
      {loc.postal || 'N/A'}
    </Descriptions.Item>
     <Descriptions.Item label="Borders">
      {loc.borders || 'N/A'}
    </Descriptions.Item>
    {/*  GEOGRAPHIC COORDINATES */}
    <Descriptions.Item label={<strong> GEOGRAPHIC COORDINATES</strong>} span={2} />
    
    <Descriptions.Item label="Latitude">
      {loc.latitude ?? 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Longitude">
      {loc.longitude ?? 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Continent">
      {loc.continent || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Continent Code">
      {loc.continent_code || 'N/A'}
    </Descriptions.Item>

    {/*  CONNECTION INFORMATION */}
    <Descriptions.Item label={<strong> CONNECTION INFORMATION</strong>} span={2} />
    
    <Descriptions.Item label="ASN">
      {loc.connection?.asn ?? 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Organization">
      {loc.connection?.org || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="ISP">
      {loc.connection?.isp || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Domain">
      {loc.connection?.domain || 'N/A'}
    </Descriptions.Item>

    {/*  TIMEZONE INFORMATION */}
    <Descriptions.Item label={<strong> TIMEZONE INFORMATION</strong>} span={2} />
    
    <Descriptions.Item label="Timezone">
      {loc.timezone?.id || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Abbreviation">
      {loc.timezone?.abbr || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="UTC Offset">
      {loc.timezone?.utc || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Offset (hours)">
      {loc.timezone?.offset ?? 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Daylight Saving">
      {loc.timezone?.is_dst ? 'Yes' : 'No'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Current Time" span={2}>
      {loc.timezone?.current_time || 'N/A'}
    </Descriptions.Item>

    {/*  CURRENCY INFORMATION */}
    <Descriptions.Item label={<strong> CURRENCY INFORMATION</strong>} span={2} />
    
    <Descriptions.Item label="Currency Name">
      {loc.currency?.name || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Currency Code">
      {loc.currency?.code || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Symbol">
      {loc.currency?.symbol || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Plural Form">
      {loc.currency?.plural || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Exchange Rate" span={2}>
      {loc.currency?.exchange_rate ?? 'N/A'}
    </Descriptions.Item>

    {/*  SECURITY INFORMATION */}
    <Descriptions.Item label={<strong> SECURITY INFORMATION</strong>} span={2} />
    
    <Descriptions.Item label="Anonymous">
      {loc.security?.anonymous ? ' Yes' : 'No'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Proxy">
      {loc.security?.proxy ? ' Yes' : 'No'}
    </Descriptions.Item>
    
    <Descriptions.Item label="VPN">
      {loc.security?.vpn ? ' Yes' : 'No'}
    </Descriptions.Item>
    
    <Descriptions.Item label="TOR">
      {loc.security?.tor ? ' Yes' : 'No'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Hosting" span={2}>
      {loc.security?.hosting ? ' Yes' : 'No'}
    </Descriptions.Item>

    {/*  FLAG INFORMATION */}
    <Descriptions.Item label={<strong> FLAG INFORMATION</strong>} span={2} />
    
    <Descriptions.Item label="Emoji">
      {loc.flag?.emoji || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Emoji Unicode">
      {loc.flag?.emoji_unicode || 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Flag Image" span={2}>
      {loc.flag?.img ? (
        <img src={loc.flag.img} alt="flag" width={40} style={{ border: '1px solid #ddd', borderRadius: 4 }} />
      ) : 'N/A'}
    </Descriptions.Item>

    {/*  RATE LIMIT INFORMATION */}
    <Descriptions.Item label={<strong> RATE LIMIT INFORMATION</strong>} span={2} />
    
    <Descriptions.Item label="Rate Limit">
      {loc.rate?.limit ?? 'N/A'}
    </Descriptions.Item>
    
    <Descriptions.Item label="Remaining">
      {loc.rate?.remaining ?? 'N/A'}
    </Descriptions.Item>

  </Descriptions>
</Panel>
                    </>
                )}
            </Collapse>
        </Card>
    );
};

export default DetailsTab;
